import {
  createWorkerOptions,
  exceedsMaxSize,
  MIN_RETRY_QUALITY,
  normalizeCompressionOptions,
  normalizeQuality,
  RETRY_QUALITY_STEP
} from './options.js';
import { getCompressionMode, SIZE_FIRST_MODE } from './presets.js';
import { runCompressionWorker } from './worker-client.js';

let activeCompressionCount = 0;
const compressionQueue = [];

export {
  COMPRESSION_MODES,
  DEFAULT_COMPRESSION_MODE,
  SIZE_FIRST_MODE,
  getCompressionMode
} from './presets.js';
export { createDefaultWorker } from './worker-client.js';

/**
 * 压缩浏览器图片文件。
 * @param {File|Blob|string} file 待压缩图片，支持 File、Blob、base64 字符串和图片链接。
 * @param {object} options 压缩参数，debug 为 true 时打印压缩格式调试信息。
 * @returns {Promise<object>} 压缩结果。
 */
export async function compressImage(file, options = {}) {
  const startedAt = performance.now();
  const normalizedOptions = normalizeCompressionOptions(options);
  try {
    return await runQueuedCompression(normalizedOptions, () =>
      compressImageImmediately(file, options, normalizedOptions)
    );
  } catch (error) {
    writeCompressConsole(
      normalizedOptions.consoleOptions,
      '压缩失败',
      createCompressConsoleData(
        getPackageCompressLogContext(file, normalizedOptions),
        null,
        performance.now() - startedAt,
        error
      )
    );
    throw error;
  }
}

/**
 * 在压缩队列中执行任务，限制同时运行的 worker 压缩数量。
 * @param {object} normalizedOptions 规范化后的压缩参数。
 * @param {Function} task 需要执行的压缩任务。
 * @returns {Promise<object>} 压缩结果。
 */
async function runQueuedCompression(normalizedOptions, task) {
  await waitForCompressionSlot(normalizedOptions);
  try {
    checkAbortSignal(normalizedOptions.signal);
    return await task();
  } finally {
    activeCompressionCount -= 1;
    runNextCompressionTask();
  }
}

/**
 * 等待压缩队列空位。
 * @param {object} normalizedOptions 规范化后的压缩参数。
 * @returns {Promise<void>} 有可用空位时 resolve。
 */
function waitForCompressionSlot(normalizedOptions) {
  return new Promise((resolve, reject) => {
    const signal = normalizedOptions.signal;
    const entry = {
      concurrency: normalizedOptions.concurrency,
      resolve,
      reject,
      signal,
      onAbort: null
    };

    entry.onAbort = () => {
      removeCompressionQueueEntry(entry);
      reject(createAbortError());
    };

    // 排队前已经中断时，不再进入压缩队列。
    if (signal && signal.aborted) {
      reject(createAbortError());
      return;
    }

    // 等待队列中的任务被中断时，需要从队列中移除。
    if (signal) {
      signal.addEventListener('abort', entry.onAbort, { once: true });
    }

    compressionQueue.push(entry);
    runNextCompressionTask();
  });
}

/**
 * 推进压缩队列中的下一个任务。
 * @returns {void}
 */
function runNextCompressionTask() {
  // 队列为空时无需调度。
  if (!compressionQueue.length) return;

  const entry = compressionQueue[0];

  // 当前运行数量达到任务限制时继续等待。
  if (activeCompressionCount >= entry.concurrency) return;

  compressionQueue.shift();
  cleanupCompressionQueueEntry(entry);
  activeCompressionCount += 1;
  entry.resolve();
}

/**
 * 从压缩队列中移除指定任务。
 * @param {object} entry 队列任务。
 * @returns {void}
 */
function removeCompressionQueueEntry(entry) {
  const index = compressionQueue.indexOf(entry);

  // 未在队列中的任务可能已经开始执行，无需重复移除。
  if (index === -1) return;

  compressionQueue.splice(index, 1);
  cleanupCompressionQueueEntry(entry);
}

/**
 * 清理压缩队列任务上的中断监听。
 * @param {object} entry 队列任务。
 * @returns {void}
 */
function cleanupCompressionQueueEntry(entry) {
  // 没有中断信号时无需清理监听。
  if (!entry.signal || !entry.onAbort) return;

  entry.signal.removeEventListener('abort', entry.onAbort);
}

/**
 * 立即执行图片压缩，不再进入队列。
 * @param {File|Blob|string} file 待压缩图片。
 * @param {object} options 用户传入的压缩参数。
 * @param {object} normalizedOptions 规范化后的压缩参数。
 * @returns {Promise<object>} 压缩结果。
 */
async function compressImageImmediately(file, options, normalizedOptions) {
  checkAbortSignal(normalizedOptions.signal);
  const imageFile = await normalizeImageInput(file);

  checkAbortSignal(normalizedOptions.signal);
  const attempts = [];
  const firstResult = await runAttempt(
    imageFile,
    normalizedOptions,
    normalizedOptions.workerOptions,
    normalizedOptions.mode,
    attempts
  );

  // worker 已返回预处理兜底结果时，不再继续重试真正压缩。
  if (isPreprocessFallbackResult(firstResult)) {
    return finishCompressionResult(
      imageFile,
      options,
      createFinalResult(
        imageFile,
        normalizedOptions,
        firstResult,
        attempts,
        normalizedOptions.mode,
        false
      )
    );
  }

  // 首次结果未超过最大体积时直接返回。
  if (!exceedsMaxSize(normalizedOptions, firstResult.blob)) {
    return finishCompressionResult(
      imageFile,
      options,
      createFinalResult(
        imageFile,
        normalizedOptions,
        firstResult,
        attempts,
        normalizedOptions.mode,
        false
      )
    );
  }

  const sizeFirstMode = getCompressionMode(SIZE_FIRST_MODE);
  let quality = Math.min(sizeFirstMode.quality, normalizedOptions.quality);
  let latestResult = firstResult;

  while (quality >= MIN_RETRY_QUALITY) {
    checkAbortSignal(normalizedOptions.signal);
    const workerOptions = createWorkerOptions(sizeFirstMode, quality, {
      ...options,
      format: normalizedOptions.format,
      maxWidth: normalizedOptions.maxWidth,
      maxHeight: normalizedOptions.maxHeight
    });
    latestResult = await runAttempt(
      imageFile,
      normalizedOptions,
      workerOptions,
      SIZE_FIRST_MODE,
      attempts
    );

    // worker 已返回预处理兜底结果时，不再继续重试真正压缩。
    if (isPreprocessFallbackResult(latestResult)) {
      return finishCompressionResult(
        imageFile,
        options,
        createFinalResult(
          imageFile,
          normalizedOptions,
          latestResult,
          attempts,
          SIZE_FIRST_MODE,
          true
        )
      );
    }

    // 当前结果已满足最大体积限制时停止重试。
    if (!exceedsMaxSize(normalizedOptions, latestResult.blob)) {
      return finishCompressionResult(
        imageFile,
        options,
        createFinalResult(
          imageFile,
          normalizedOptions,
          latestResult,
          attempts,
          SIZE_FIRST_MODE,
          true
        )
      );
    }

    quality -= RETRY_QUALITY_STEP;
  }

  return finishCompressionResult(
    imageFile,
    options,
    createFinalResult(
      imageFile,
      normalizedOptions,
      latestResult,
      attempts,
      SIZE_FIRST_MODE,
      true
    )
  );
}

/**
 * 检查压缩任务是否已经被中断。
 * @param {AbortSignal|null} signal 中断信号。
 * @returns {void}
 * @throws {Error} 已中断时抛出 AbortError。
 */
function checkAbortSignal(signal) {
  // 没有传中断信号时保持原流程。
  if (!signal) return;

  // 信号已中断时停止后续压缩步骤。
  if (signal.aborted) {
    throw createAbortError();
  }
}

/**
 * 创建压缩中断错误。
 * @returns {Error} AbortError。
 */
function createAbortError() {
  const error = new Error('图片压缩已中断');
  error.name = 'AbortError';
  return error;
}

/**
 * 判断压缩结果是否为尺寸处理后的兜底结果。
 * @param {object} result 单次压缩结果。
 * @returns {boolean} 是预处理兜底结果时返回 true。
 */
function isPreprocessFallbackResult(result) {
  return !!(result && result.compressionFallback);
}

/**
 * 输出压缩调试日志。
 * @param {object|null} consoleOptions 压缩调试日志配置。
 * @param {string} title 日志标题。
 * @param {object} data 日志数据。
 * @returns {void}
 */
function writeCompressConsole(consoleOptions, title, data = {}) {
  // 未传入调试配置时保持静默。
  if (!consoleOptions || typeof consoleOptions.console !== 'function') return;

  consoleOptions.console({
    page: consoleOptions.page || 'packages/image-compress',
    method: consoleOptions.method || 'compressImage',
    title,
    data: {
      ...(consoleOptions.data || {}),
      ...data
    }
  });
}

/**
 * 将压缩输入统一转换为 Blob 或 File。
 * @param {File|Blob|string} file 待压缩图片，支持 File、Blob、base64 字符串和图片链接。
 * @returns {Promise<File|Blob>} 可传给 worker 的图片对象。
 */
async function normalizeImageInput(file) {
  // Blob 和 File 可以直接交给 worker 处理。
  if (file instanceof Blob) {
    return normalizeBlobImageInput(file, file.name || 'image');
  }

  // 字符串输入按 base64 或图片链接解析。
  if (typeof file === 'string') {
    return resolveStringImageInput(file);
  }

  throw new TypeError(
    'compressImage 需要传入 File、Blob、base64 字符串或图片链接'
  );
}

/**
 * 解析字符串图片输入。
 * @param {string} input base64 字符串或图片链接。
 * @returns {Promise<File|Blob>} 可传给 worker 的图片对象。
 */
async function resolveStringImageInput(input) {
  const source = input.trim();

  // 空字符串不是有效图片来源。
  if (!source) {
    throw new TypeError('compressImage 需要传入有效的 base64 字符串或图片链接');
  }

  // data URL 直接通过浏览器 fetch 转为 Blob。
  if (source.startsWith('data:')) {
    return fetchImageSource(source, 'image');
  }

  // 普通图片链接通过 fetch 拉取后转为 Blob。
  if (isImageUrl(source)) {
    return fetchImageSource(source, getFileNameFromUrl(source));
  }

  return createBlobFromBase64(source);
}

/**
 * 判断字符串是否为浏览器可拉取的图片链接。
 * @param {string} source 输入字符串。
 * @returns {boolean} 是图片链接时返回 true。
 */
function isImageUrl(source) {
  // 明确协议的图片来源直接按链接处理。
  if (/^(https?:|blob:|\/\/)/.test(source)) return true;

  return /^(\/|\.\/|\.\.\/).+\.(jpe?g|png|webp|avif|gif|bmp)([?#].*)?$/i.test(
    source
  );
}

/**
 * 通过 fetch 拉取图片来源。
 * @param {string} source 图片来源。
 * @param {string} name 文件名。
 * @returns {Promise<File|Blob>} 图片 Blob 或 File。
 */
async function fetchImageSource(source, name) {
  const response = await fetch(source);

  // 拉取失败时抛出明确错误。
  if (!response.ok) {
    throw new Error(`图片加载失败：${response.status}`);
  }

  const blob = await response.blob();
  const normalizedBlob = await normalizeBlobImageInput(blob, name || 'image');

  // 真实 MIME 校正后若已返回 File，直接复用该文件。
  if (typeof File === 'function' && normalizedBlob instanceof File) {
    return normalizedBlob;
  }

  // File 可保留名称，便于 auto 格式按扩展名判断。
  if (typeof File === 'function') {
    return new File([normalizedBlob], name || 'image', {
      type: normalizedBlob.type
    });
  }

  return normalizedBlob;
}

/**
 * 归一化 Blob 图片输入，优先按真实文件头修正 MIME 类型。
 * @param {Blob|File} blob 图片 Blob 或 File。
 * @param {string} name 图片名称，用于保留 File 文件名。
 * @returns {Promise<Blob|File>} MIME 已尽量校正后的图片对象。
 */
async function normalizeBlobImageInput(blob, name) {
  const realMimeType = await detectBlobImageMimeType(blob);
  const mimeType = realMimeType || blob.type || '';

  // 真实 MIME 与当前 Blob MIME 一致时，直接复用原对象。
  if (mimeType === (blob.type || '')) {
    return blob;
  }

  // 浏览器支持 File 时保留文件名，便于后续扩展名兜底判断。
  if (typeof File === 'function') {
    return new File([blob], name || blob.name || 'image', {
      type: mimeType,
      lastModified: blob.lastModified || Date.now()
    });
  }

  return new Blob([blob], { type: mimeType });
}

/**
 * 从 Blob 文件头读取真实图片 MIME 类型。
 * @param {Blob} blob 图片 Blob。
 * @returns {Promise<string>} 识别到的 MIME 类型，无法识别时返回空字符串。
 */
async function detectBlobImageMimeType(blob) {
  try {
    const buffer = await blob.slice(0, 16).arrayBuffer();
    return detectImageMimeType(new Uint8Array(buffer));
  } catch (error) {
    return '';
  }
}

/**
 * 将纯 base64 字符串转为 Blob。
 * @param {string} source base64 字符串。
 * @returns {Blob} 图片 Blob。
 */
function createBlobFromBase64(source) {
  const base64 = source.replace(/\s/g, '');
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new Blob([bytes], { type: detectImageMimeType(bytes) });
}

/**
 * 根据图片文件头推断 MIME 类型。
 * @param {Uint8Array} bytes 图片二进制。
 * @returns {string} MIME 类型，无法识别时返回空字符串。
 */
function detectImageMimeType(bytes) {
  // JPEG 文件头为 FF D8 FF。
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff)
    return 'image/jpeg';

  // PNG 文件头为完整 8 字节签名。
  if (
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  )
    return 'image/png';

  // WebP 位于 RIFF 容器中。
  if (
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  )
    return 'image/webp';

  // AVIF 通常包含 ftyp avif 或 ftyp avis 标记。
  if (
    bytes[4] === 0x66 &&
    bytes[5] === 0x74 &&
    bytes[6] === 0x79 &&
    bytes[7] === 0x70 &&
    bytes[8] === 0x61 &&
    bytes[9] === 0x76 &&
    bytes[10] === 0x69 &&
    (bytes[11] === 0x66 || bytes[11] === 0x73)
  )
    return 'image/avif';

  return '';
}

/**
 * 从 URL 中提取文件名。
 * @param {string} source 图片链接。
 * @returns {string} 文件名。
 */
function getFileNameFromUrl(source) {
  try {
    const url = new URL(source, location.href);
    const name = url.pathname.split('/').pop();

    // URL 路径没有文件名时使用默认名称。
    if (!name) return 'image';

    return name;
  } catch (error) {
    return 'image';
  }
}

/**
 * 执行单次压缩尝试。
 * @param {File|Blob} file 待压缩图片文件。
 * @param {object} normalizedOptions 规范化后的压缩参数。
 * @param {object} workerOptions worker 压缩参数。
 * @param {string} mode 当前压缩模式。
 * @param {Array<object>} attempts 压缩尝试记录。
 * @returns {Promise<object>} 单次压缩结果。
 */
async function runAttempt(
  file,
  normalizedOptions,
  workerOptions,
  mode,
  attempts
) {
  const startedAt = performance.now();
  const result = await runCompressionWorker(file, workerOptions, {
    timeout: normalizedOptions.timeout,
    signal: normalizedOptions.signal,
    consoleOptions: null,
    createWorker: normalizedOptions.createWorker
  });
  let resultQuality = result.quality;

  // worker 未返回质量时，阈值跳过无编码质量；正常编码沿用 worker 入参质量。
  if (resultQuality === undefined) {
    resultQuality = result.thresholdSkipped ? null : workerOptions.quality;
  }

  const attempt = {
    mode,
    quality: resultQuality,
    size: result.encodedSize || result.blob.size,
    width: result.width,
    height: result.height,
    durationMs: result.durationMs || performance.now() - startedAt,
    encoder: result.encoder,
    thresholdSkipped: Boolean(result.thresholdSkipped)
  };
  attempts.push(attempt);

  return {
    ...result,
    mode,
    quality: resultQuality,
    compressedSize: result.encodedSize || result.blob.size,
    thresholdSkipped: Boolean(result.thresholdSkipped)
  };
}

/**
 * 创建最终对外返回的压缩结果。
 *
 * 这个函数会先把 worker 返回的压缩结果整理成统一结构，然后再做一次“是否保留原图”的兜底判断。
 * 默认兜底场景主要针对“只重新编码、不缩放尺寸”的情况：如果编码后的体积反而大于等于原图，
 * 就直接返回原图，避免调用方拿到一张越压越大的图片。调用方也可以显式声明尺寸限制非强制，
 * 允许有尺寸处理要求时仍按体积结果回退原图。
 *
 * 注意：如果用户传了 maxWidth、maxHeight，或者同时传了 customWidth/customHeight，
 * 说明这次压缩有明确的尺寸处理要求。即使最终体积没有变小，也会保留压缩/缩放后的结果，
 * 不会自动回退到原图，除非 useOriginalWhenCompressedLarger 为 true。
 *
 * @param {File|Blob} imageFile 归一化后的原始图片输入，可能是 File 或 Blob。
 * @param {object} options 归一化后的压缩参数，用于判断是否存在尺寸缩放要求。
 * @param {object} result worker 最后一次压缩返回的结果。
 * @param {Array<object>} attempts 所有压缩尝试记录，用于统计耗时、质量、尺寸等信息。
 * @param {string} mode 最终使用的压缩模式。
 * @param {boolean} sizeLimited 是否触发过按目标体积重试的逻辑。
 * @returns {object} 对外暴露的最终压缩结果。
 */
function createFinalResult(
  imageFile,
  options,
  result,
  attempts,
  mode,
  sizeLimited
) {
  // 先按常规压缩结果结构整理字段，例如 blob、宽高、体积、耗时、encoder、attempts 等。
  const compressedResult = createResult(result, attempts, mode, sizeLimited);
  const originalSize =
    imageFile && typeof imageFile.size === 'number' ? imageFile.size : 0;
  const compressedBlobSize =
    compressedResult.blob && typeof compressedResult.blob.size === 'number'
      ? compressedResult.blob.size
      : 0;
  const compressedLargerSize =
    originalSize && compressedBlobSize
      ? Math.max(0, compressedBlobSize - originalSize)
      : 0;
  const compressedLarger = compressedLargerSize > 0;

  // 如果不需要回退原图，就直接返回压缩结果，并明确标记 usedOriginal=false。
  if (!shouldUseOriginalImage(imageFile, options, compressedResult.blob)) {
    return {
      ...compressedResult,
      compressedLarger,
      compressedLargerSize,
      usedOriginal: false
    };
  }

  // 走到这里表示：本次允许体积反增时回退原图，并且重新编码后的体积不小于原图。
  // 因此最终 blob 换回原始 imageFile，但保留 originalWidth/originalHeight 等诊断信息。
  return {
    ...compressedResult,
    blob: imageFile,
    format: getOriginalFormat(imageFile, compressedResult.format),
    actualMimeType: imageFile.type || compressedResult.actualMimeType,
    width: compressedResult.originalWidth,
    height: compressedResult.originalHeight,
    compressedSize: imageFile.size,
    encodedSize: imageFile.size,
    encoder: 'Original file',
    quality: compressedResult.quality,
    compressedLarger,
    compressedLargerSize,
    usedOriginal: true
  };
}

/**
 * 判断最终是否应该直接返回原图。
 *
 * 默认只有在“没有尺寸处理要求”的时候才允许回退原图。
 * 原因是：不缩放时，压缩动作本质上只是重新编码，重新编码可能因为格式、调色板、元数据、
 * PNG 无损压缩策略等原因导致体积变大。此时返回原图更符合“压缩不能越压越大”的直觉。
 *
 * 如果存在 maxWidth/maxHeight/customWidth/customHeight，则用户关心的不只是体积，
 * 还包括输出尺寸，所以不能因为体积没变小就擅自丢弃缩放后的结果。调用方显式声明
 * useOriginalWhenCompressedLarger 时，表示尺寸限制非强制，可在压缩体积反增时回退原图。
 *
 * @param {File|Blob} imageFile 归一化后的原始图片输入。
 * @param {object} options 归一化后的压缩参数。
 * @param {boolean} options.useOriginalWhenCompressedLarger 尺寸限制非强制时，是否允许压缩后体积不小于原图时回退原图。
 * @param {Blob} compressedBlob worker 编码后的图片 Blob。
 * @returns {boolean} true 表示最终应该返回原图，false 表示返回压缩结果。
 */
function shouldUseOriginalImage(imageFile, options, compressedBlob) {
  // maxWidth/maxHeight 表示按最大宽高等比缩放；customWidth/customHeight 同时存在时表示强制指定宽高。
  const hasResizeRequirement =
    Boolean(options.maxWidth) ||
    Boolean(options.maxHeight) ||
    Boolean(options.customWidth && options.customHeight);
  const hasFormatRequirement = options.format && options.format !== 'auto';
  const canIgnoreResizeRequirement =
    options.useOriginalWhenCompressedLarger === true;

  // 显式指定输出格式时不能回退原图，否则可能破坏格式契约。
  if (
    hasFormatRequirement &&
    (!canIgnoreResizeRequirement ||
      options.format !== getOriginalFormat(imageFile, ''))
  ) {
    return false;
  }

  // 尺寸要求非强制或没有尺寸要求，并且压缩结果体积大于等于原图时，才使用原图。
  return (
    (!hasResizeRequirement || canIgnoreResizeRequirement) &&
    imageFile &&
    compressedBlob &&
    typeof imageFile.size === 'number' &&
    compressedBlob.size >= imageFile.size
  );
}

/**
 * 获取原图格式，用于回退原图时修正返回结果里的 format 字段。
 *
 * worker 的 result.format 表示“编码器实际输出的格式”。但如果最终回退为原图，
 * 返回结果的 blob 已经不是 worker 编码后的 blob 了，所以 format 也应该尽量改回原图格式。
 * 优先使用 File/Blob 的 MIME type；如果 MIME 不可靠，再从文件名扩展名推断。
 *
 * @param {File|Blob} imageFile 归一化后的原始图片输入。
 * @param {string} fallback worker 编码结果中的格式，无法识别原图格式时使用它兜底。
 * @returns {string} 原图格式，例如 jpeg、png、webp、avif。
 */
function getOriginalFormat(imageFile, fallback) {
  // 优先从 MIME 中解析，例如 image/jpeg -> jpeg、image/png -> png。
  const mimeType = String(imageFile.type || '').toLowerCase();
  const mimeSubtype =
    mimeType.indexOf('image/') === 0
      ? mimeType.slice('image/'.length).split(';')[0]
      : '';

  if (mimeSubtype === 'jpg') return 'jpeg';
  if (mimeSubtype) return mimeSubtype === 'svg+xml' ? 'svg' : mimeSubtype;

  // MIME 不存在时，再从文件名扩展名推断。
  const name = String(imageFile.name || '')
    .toLowerCase()
    .split(/[?#]/)[0];
  const extension = name.indexOf('.') > -1 ? name.split('.').pop() : '';

  if (extension === 'jpg') return 'jpeg';
  return extension || fallback;
}

/**
 * 返回压缩结果，并在 debug 模式下打印格式调试信息。
 * @param {File|Blob} imageFile 归一化后的原始图片输入。
 * @param {object} options 用户传入的压缩参数。
 * @param {object} result 压缩结果。
 * @returns {object} 压缩结果。
 */
function finishCompressionResult(imageFile, options, result) {
  writeCompressConsole(
    options.consoleOptions,
    '压缩完成',
    createCompressConsoleData(
      getPackageCompressLogContext(imageFile, options),
      result,
      result.durationMs || 0
    )
  );

  // debug 未开启时保持默认静默行为。
  if (!options.debug) {
    return result;
  }

  printCompressionDebugInfo(imageFile, result);
  return result;
}

/**
 * 打印图片压缩格式调试信息。
 * @param {File|Blob} imageFile 归一化后的原始图片输入。
 * @param {object} result 压缩结果。
 * @returns {void}
 */
function printCompressionDebugInfo(imageFile, result) {
  console.log('图片压缩格式信息', {
    input: getCompressionInputDebugInfo(imageFile),
    output: getCompressionOutputDebugInfo(result)
  });
}

/**
 * 获取压缩输入格式调试信息。
 * @param {File|Blob} imageFile 归一化后的原始图片输入。
 * @returns {object} 输入格式调试信息。
 */
function getCompressionInputDebugInfo(imageFile) {
  return {
    format: getOriginalFormat(imageFile, ''),
    mimeType: imageFile.type || '',
    fileName: imageFile.name || '',
    size: imageFile.size || 0
  };
}

/**
 * 获取压缩输出格式调试信息。
 * @param {object} result 压缩结果。
 * @returns {object} 输出格式调试信息。
 */
function getCompressionOutputDebugInfo(result) {
  const encoder = (result && result.encoder) || '';
  return {
    encoder,
    compressType: getCompressionEncoderType(encoder),
    format: result && result.format,
    requestedFormat: result && result.requestedFormat,
    actualMimeType: result && result.actualMimeType,
    blobType: result && result.blob && result.blob.type,
    blobSize: result && result.blob && result.blob.size,
    attempts: result && result.attempts
  };
}

/**
 * 根据编码器名称获取压缩类型。
 * @param {string} encoder 编码器名称。
 * @returns {string} JSQuash、Canvas、Original 或 unknown。
 */
function getCompressionEncoderType(encoder) {
  // JSQuash 编码器名称统一包含 JSQuash。
  if (encoder.indexOf('JSQuash') > -1) {
    return 'JSQuash';
  }

  // Canvas 相关编码器可能是英文 Native Canvas 或中文原生编码。
  if (encoder.indexOf('Canvas') > -1 || encoder.indexOf('原生') > -1) {
    return 'Canvas';
  }

  // 回退原图时明确标识为 Original。
  if (encoder.indexOf('Original') > -1) {
    return 'Original';
  }

  return encoder || 'unknown';
}

/**
 * 创建底层压缩日志上下文。
 * @param {File|Blob|string} imageFile 图片输入。
 * @param {object} options 压缩选项。
 * @returns {object} 日志上下文。
 */
function getPackageCompressLogContext(imageFile, options) {
  return {
    originalSizeBytes:
      imageFile && typeof imageFile.size === 'number' ? imageFile.size : 0,
    naturalWidth: 0,
    naturalHeight: 0,
    outputWidth: 0,
    outputHeight: 0,
    format: options.format || 'auto',
    maxSizeMB:
      options.maxSizeMB === undefined && options.maxSizeBytes
        ? (options.maxSizeBytes / 1024 / 1024).toFixed(2)
        : options.maxSizeMB,
    forceNormalEncodeCompression:
      options.forceNormalEncodeCompression === true ||
      !!(
        options.workerOptions &&
        options.workerOptions.forceNormalEncodeCompression
      ),
    orientation: 1,
    usedOrientationCanvas: false
  };
}

/**
 * 格式化底层压缩尺寸。
 * @param {number} width 图片宽度。
 * @param {number} height 图片高度。
 * @returns {string} 尺寸文案。
 */
function formatCompressDimension(width, height) {
  return width && height ? width + 'x' + height : '未生成';
}

/**
 * 格式化底层压缩体积。
 * @param {number} size 图片字节数。
 * @returns {string} MB 体积文案。
 */
function formatCompressSizeMB(size) {
  return typeof size === 'number' && size > 0
    ? (size / 1024 / 1024).toFixed(2) + 'MB'
    : '未知';
}

/**
 * 获取底层压缩方案文案。
 * @param {object} context 日志上下文。
 * @param {object|null} result 压缩结果。
 * @param {Error|null} error 压缩错误。
 * @returns {string} 压缩方案文案。
 */
function getCompressSchemeText(context, result, error) {
  // 压缩失败且没有可用结果时，标记为未完成。
  if (error) {
    return '未完成';
  }

  return [
    result.sizeLimited ? '大小优先压缩' : '速度优先压缩',
    context.forceNormalEncodeCompression ? '强制正常编码' : '默认编码',
    result.compressionFallback ? '兜底结果' : '正常结果'
  ].join(' / ');
}

/**
 * 获取底层压缩原因说明。
 * @param {object} context 日志上下文。
 * @param {object|null} result 压缩结果。
 * @param {Error|null} error 压缩错误。
 * @returns {string} 压缩原因说明。
 */
function getCompressReasonText(context, result, error) {
  const reasons = [];
  const maxSizeText = context.maxSizeMB ? context.maxSizeMB + 'MB' : '未限制';

  // 压缩失败时直接记录错误原因。
  if (error) {
    return '压缩失败：' + ((error && error.message) || String(error));
  }

  // 强制正常编码会禁用底层阈值跳过和大像素跳过。
  if (context.forceNormalEncodeCompression) {
    reasons.push(
      'forceNormalEncodeCompression=true，强制进入正常编码，不跳过真正压缩'
    );
  }

  const firstAttempt = result.attempts && result.attempts[0];
  const firstSizeText = formatCompressSizeMB(
    firstAttempt ? firstAttempt.size : result.encodedSize
  );
  const finalSizeText = formatCompressSizeMB(result.blob && result.blob.size);
  const compressedLargerText = result.compressedLarger
    ? formatCompressSizeMB(result.compressedLargerSize)
    : '0.00MB';

  // 首次压缩结果超出体积上限时，底层会切换为大小优先。
  if (result.sizeLimited) {
    reasons.push(
      '首次压缩结果 ' +
        firstSizeText +
        ' 超出 maxSizeMB=' +
        maxSizeText +
        '，因此切换为大小优先压缩；最终结果 ' +
        finalSizeText
    );
  } else {
    reasons.push(
      '压缩结果 ' +
        finalSizeText +
        ' 未触发 maxSizeMB=' +
        maxSizeText +
        ' 的大小优先压缩'
    );
  }

  // 回退原图时说明最终没有使用重编码结果。
  if (result.usedOriginal) {
    reasons.push(
      '重新编码结果不小于原图，变大体积=' +
        compressedLargerText +
        '，因此返回原图'
    );
  }

  // 使用兜底结果时，补充具体兜底原因。
  if (result.compressionFallback) {
    let fallbackText =
      '使用兜底结果，fallbackReason=' + (result.fallbackReason || '未知');

    // 大像素保护兜底需要说明实际像素和上限。
    if (result.fallbackReason === 'largePixels') {
      fallbackText +=
        '，输出像素=' +
        result.pixels +
        '，JS 编码上限=' +
        result.maxJsEncodePixels;
    }

    reasons.push(fallbackText);
  }

  return reasons.join('；');
}

/**
 * 创建底层最终压缩日志数据。
 * @param {object} context 日志上下文。
 * @param {object|null} result 压缩结果。
 * @param {number} duration 压缩耗时，单位毫秒。
 * @param {Error|null} [error=null] 压缩错误。
 * @returns {object} 压缩日志数据。
 */
function createCompressConsoleData(context, result, duration, error = null) {
  const originalWidth = result ? result.originalWidth : context.naturalWidth;
  const originalHeight = result ? result.originalHeight : context.naturalHeight;
  const outputWidth = result ? result.width || context.outputWidth : 0;
  const outputHeight = result ? result.height || context.outputHeight : 0;
  const outputSize = result && result.blob ? result.blob.size : 0;

  return {
    压缩变化: {
      尺寸:
        formatCompressDimension(originalWidth, originalHeight) +
        ' -> ' +
        formatCompressDimension(outputWidth, outputHeight),
      体积:
        formatCompressSizeMB(context.originalSizeBytes) +
        ' -> ' +
        formatCompressSizeMB(outputSize),
      压缩后是否变大: Boolean(result && result.compressedLarger),
      压缩后变大体积:
        result && result.compressedLarger
          ? formatCompressSizeMB(result.compressedLargerSize)
          : '0.00MB'
    },
    压缩方案: getCompressSchemeText(context, result, error),
    压缩耗时: Math.round(duration) + 'ms',
    图片格式: (result && result.format) || context.format,
    压缩质量: result && result.quality !== undefined ? result.quality : '未知',
    采用这个压缩方案的原因: getCompressReasonText(context, result, error)
  };
}

/**
 * 创建对外返回的压缩结果。
 * @param {object} result worker 压缩结果。
 * @param {Array<object>} attempts 压缩尝试列表。
 * @param {string} mode 最终使用的压缩模式。
 * @param {boolean} sizeLimited 是否触发大小限制重试。
 * @returns {object} 对外压缩结果。
 */
function createResult(result, attempts, mode, sizeLimited) {
  const durationMs = attempts.reduce(
    (sum, attempt) => sum + (attempt.durationMs || 0),
    0
  );

  return {
    blob: result.blob,
    format: result.format,
    requestedFormat: result.requestedFormat,
    actualMimeType: result.actualMimeType || result.blob.type,
    width: result.width,
    height: result.height,
    originalWidth: result.originalWidth,
    originalHeight: result.originalHeight,
    compressedSize: result.compressedSize || result.blob.size,
    encodedSize: result.compressedSize || result.blob.size,
    durationMs,
    attempts: attempts.map((attempt) => ({ ...attempt })),
    encoder: result.encoder,
    mode,
    quality: result.quality,
    sizeLimited,
    thresholdSkipped: Boolean(result.thresholdSkipped),
    compressionFallback: Boolean(result.compressionFallback),
    fallbackReason: result.fallbackReason,
    pixels: result.pixels,
    maxJsEncodePixels: result.maxJsEncodePixels
  };
}
